import { AtarimEditorRef } from "editor";
import { MoveUp, SendHorizontal, StickyNote } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { GetTaskResponse } from "types/api/collaborate";
import { Button } from "ui";
import { isHTMLEmpty } from "utils/proxyUtils";
import { getSelectedSiteCollaborateInfo } from "../../../../store/siteInfoSlice";
import { getIsAIActive, getNotifiedUsers, getSelectedTask, NotifyuserProps } from "../../../../store/tasksSlice";
import CompleteTaskButton from "./complete-task-button";
import MakeInternalButton from "./make-internal-button";
import UnsetInternalButton from "./unset-internal-button";

interface Props {
  existingTask?: GetTaskResponse;
  editorRef: React.RefObject<AtarimEditorRef | null>;
  isLoading?: boolean;
  handleCreate: (note: boolean, comment: string, files: File[]) => void;
  files: File[];
  comment: string;
  setComment: React.Dispatch<React.SetStateAction<string>>;
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
}

// const lockTitle = "Content Management"
// const lockDescription = "Edit or delete comments with ease, keeping tasks clear, organized, and aligned with your workflow.."
// const lockModalImgSetting = 'https://wpfeedback-image.s3.us-east-2.amazonaws.com/images/integration-lock-images/lock-user-image.jpg'


export const ActionBar = ({ isLoading, handleCreate, editorRef, files, comment, setComment, setFiles }: Props) => {
  const { t } = useTranslation();
  const selectedTask = useSelector(getSelectedTask);
  const notifyUsers = useSelector(getNotifiedUsers);
  const siteDetails = useSelector(getSelectedSiteCollaborateInfo);
  const isAIActive = useSelector(getIsAIActive)
  // const userInfo = useSelector(getUser)
  const [notifyUserList, setNotifyUserList] = useState<string>('')
  const isEmpty = useMemo(() => (comment === '' || isHTMLEmpty(comment)) && files.length === 0, [comment, files]);
  // const locked = !userInfo?.raw?.plan?.team_management;

  // const handleLockInfo = () => {
  //   dispatch(setShowLockFeature({
  //     showLockFeature: true,
  //     lockTitle: lockTitle,
  //     lockParagraph: lockDescription,
  //     lockImg: lockModalImgSetting,
  //     rank: 3,
  //   }));
  // }

  const getTooltip = (isNote: boolean) => {
    if (comment === '') {
      return t('action_bar.tooltip.empty_content', 'Add your comment before posting');
    }
    return isNote ?
      t('action_bar.tooltip.add_note', 'Add a comment as a note, only Admins and Contributors will be able to see') :
      t('action_bar.tooltip.submit', 'Submit your comment');
  };

  const getUsersByIds = (allUsers: NotifyuserProps[], userIds: string[]) => {
    const idNumbers = userIds.map(id => String(id));

    return allUsers.filter(user => {
      const userValueStr = String(user.value);
      const userNormalIdStr = String(user.normal_id || '');

      return idNumbers.includes(userValueStr) || idNumbers.includes(userNormalIdStr);
    });
    // return allUsers.filter(user => idNumbers.includes(String(user.value) || String(user.normal_id)));
  };
  const formatNotificationMessage = (users: NotifyuserProps[]) => {
    // Extract labels from the users array
    const labels = users.map(user => (user.label ?? '').trim());

    // Check if we have any labels
    if (labels.length === 0) {
      return "No users will be notified";
    }

    // If only one label, return simple format
    if (labels.length === 1) {
      return `${labels[0]} will be notified`;
    }

    // For multiple labels, format with commas and "& " for the last one
    const lastLabel = labels.pop();
    return `${labels.join(', ')} & ${lastLabel} will be notified`;
  }

  useEffect(() => {
    if (siteDetails && notifyUsers) {
      const allUsers = siteDetails?.result?.users
      const usersById = getUsersByIds(allUsers, notifyUsers)
      const notificationText = formatNotificationMessage(usersById)
      setNotifyUserList(notificationText)
    }
  }, [notifyUsers.length, siteDetails, notifyUsers, setNotifyUserList])

  const hasPermission = useMemo(() => {
    return siteDetails?.result?.role === 'admin' || siteDetails?.result?.role === 'contributor';
  }, [siteDetails?.result?.role]);


  return (

    <div className="flex justify-between w-full overflow-hidden">
      <AnimatePresence mode="wait">
        {!isAIActive ? (
          <motion.div

            className="flex justify-between w-full"
            key="standard-view"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ y: [0, -3, 20], opacity: [1, 1, 0] }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
          >
            <div className="flex gap-1.5">
              {selectedTask && selectedTask.task_id && hasPermission && (
                <>
                  <CompleteTaskButton taskId={selectedTask.task_id} />
                  {!!selectedTask?.is_internal === false ? (
                    <MakeInternalButton taskId={selectedTask.task_id} task={selectedTask} />
                  ) : (
                    <UnsetInternalButton taskId={selectedTask.task_id} task={selectedTask} />
                  )}
                </>
              )}
            </div>
            <div className="flex gap-1.5">
              {selectedTask && selectedTask.task_id && hasPermission && (
                <Button
                  toolTipPortal
                  tooltip={getTooltip(true)}
                  variant="outline"
                  size="icon"
                  loading={isLoading}
                  disabled={isEmpty}
                  onClick={() => {
                    // if (!locked) {
                    //   handleLockInfo()
                    //   return;
                    // }
                    handleCreate(true, comment, files)
                  }}
                  aria-label={t('action_bar.aria.add_note', 'Add internal note')}
                >
                  <StickyNote size={18} aria-hidden="true" />
                </Button>
              )}
              <Button
                size={!hasPermission ? "sm" : "icon"}
                onClick={() => handleCreate(false, comment, files)}
                loading={isLoading || selectedTask?.task_id?.includes('PENDING')}
                toolTipPortal
                tooltip={isEmpty ? getTooltip(false) : notifyUserList}
                disabled={isEmpty}
                aria-label={t('action_bar.aria.submit', 'Submit comment')}
              >
                {!hasPermission ? <span>Comment</span> :
                  <SendHorizontal size={18} aria-hidden="true" />}
              </Button>
            </div>
          </motion.div>
        ) : (
          <motion.div className="flex justify-between w-full"

            key="ai-active-view"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ y: [0, 3, -20], opacity: [1, 1, 0] }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}

          >
            <div className="flex gap-1.5">
              {selectedTask && selectedTask.task_id && hasPermission && (
                <>
                  <CompleteTaskButton taskId={selectedTask.task_id} />
                  {!selectedTask?.is_internal ? (
                    <MakeInternalButton taskId={selectedTask.task_id} task={selectedTask} />
                  ) : (
                    <UnsetInternalButton taskId={selectedTask.task_id} task={selectedTask} />
                  )}
                </>
              )}
            </div>
            <div className="flex gap-1.5">
              <Button
                size="icon"
                className="bg-tertiary text-white border-tertiary"
                tooltip='Submit your comment to the InnerCircle'
                toolTipPortal
                onClick={() => {
                  handleCreate(false, comment, files)
                  editorRef.current?.editor?.commands.clearContent();
                  setComment('');
                  setFiles([]);
                }}
                disabled={isEmpty}
              >
                <MoveUp size={18} />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>

  );
};